import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  saveErrorToast,
  useAdminFormPersistence,
  type PageTab,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useAccountHasEntitlement from "@common/hooks/useAccountHasEntitlement";
import { useV2GetVendorQuery } from "@generated";
import { VendorForm } from "../VendorForm/VendorForm";
import { TermsConditions } from "../TermsAndConditions/TermsConditions";
import { useVendorMutations } from "../api";
import { emptyVendorForm } from "../types";
import type { VendorEditTab, VendorFormValues } from "../types";

const DETAILS_FORM_ID = "vendor-form";
const TERMS_FORM_ID = "terms-conditions-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { hasEntitlement } = useAccountHasEntitlement();
  const { update } = useVendorMutations();
  // refetchOnMountOrArgChange so re-entering Edit (or arriving from a list
  // changed elsewhere) re-requests the record rather than trusting the cache.
  const { data, isLoading, refetch } = useV2GetVendorQuery(
    { id: id },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<VendorFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<VendorEditTab>("vendor");

  const tabs: PageTab[] = [
    { key: "vendor", label: "Vendor" },
    ...(hasEntitlement("terms-and-conditions")
      ? [{ key: "special_conditions", label: "Terms & Conditions" }]
      : []),
  ];

  const loadedInitial = useMemo<VendorFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyVendorForm;
    return {
      name: attrs.name ?? "",
      streetAddress: attrs["street-address"] ?? "",
      street2: attrs.street2 ?? "",
      city: attrs.city ?? "",
      state: attrs.state ?? "",
      postalCode: attrs["postal-code"] ?? "",
      country: attrs.country ?? "",
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: VendorFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update vendor", err);
      setToast(saveErrorToast(err, "vendor"));
    } finally {
      setSubmitting(false);
    }
  };

  const formId =
    activeTab === "vendor"
      ? DETAILS_FORM_ID
      : activeTab === "special_conditions"
      ? TERMS_FORM_ID
      : undefined;

  return (
    <>
      <AdminFormPage
        title="Edit Vendor"
        formId={formId}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as VendorEditTab)}
      >
        {activeTab === "vendor" && (
          <VendorForm
            key={formKey}
            id={DETAILS_FORM_ID}
            initialValues={initialValues}
            isSubmitting={submitting}
            onSubmit={handleSubmit}
          />
        )}
        {activeTab === "special_conditions" && (
          <TermsConditions
            vendorId={id}
            formId={TERMS_FORM_ID}
            onSubmittingChange={setSubmitting}
          />
        )}
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
